import {
    ArrowLeftIcon,
    ArrowsRightLeftIcon,
    CheckCircleIcon,
    DocumentTextIcon,
    PaperAirplaneIcon,
    SparklesIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { cn } from '@/utils/cn'

import {
    ConflictMessage,
    MOCK_CONFLICTS,
    MOCK_DOCUMENTS,
    MOCK_PROVIDERS,
    MOCK_ROADMAP,
    PROVIDER_TYPE_LABEL,
} from './mockData'

export default function ConflictDetail() {
    const { id } = useParams()
    const conflict = MOCK_CONFLICTS.find((c) => c.id === id)
    const [draft, setDraft] = useState('')

    if (!conflict) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>Conflict not found.</p>
                <Link
                    to='/admin/family-care-team-preview/conflicts'
                    className='text-brand-600 hover:underline'
                >
                    Back to conflicts
                </Link>
            </div>
        )
    }

    const provA = MOCK_PROVIDERS.find((p) => p.id === conflict.providerAId)
    const provB = MOCK_PROVIDERS.find((p) => p.id === conflict.providerBId)
    if (!provA || !provB) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center text-neutral-600'>
                Provider data missing for this conflict.
            </div>
        )
    }
    const docA = MOCK_DOCUMENTS.find((d) => d.id === conflict.providerADocId)
    const docB = MOCK_DOCUMENTS.find((d) => d.id === conflict.providerBDocId)
    const milestones = MOCK_ROADMAP.filter((m) => conflict.impactedMilestoneIds.includes(m.id))
    const isResolved = conflict.status === 'resolved'

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/admin/family-care-team-preview/conflicts'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to conflicts
            </Link>

            {/* Header */}
            <div className='mb-6'>
                <div className='flex flex-wrap items-center gap-2 mb-1'>
                    <ArrowsRightLeftIcon className={`w-5 h-5 ${isResolved ? 'text-green-600' : 'text-red-600'}`} />
                    {isResolved && (
                        <span
                            className={
                                'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider ' +
                                'text-green-800 bg-green-100 px-1.5 py-0.5 rounded'
                            }
                        >
                            <CheckCircleIcon className='w-3 h-3' />
                            Resolved
                        </span>
                    )}
                </div>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>{conflict.title}</h1>
                <p className='text-sm text-neutral-700 mt-2 max-w-3xl'>{conflict.summary}</p>
            </div>

            {/* Side-by-side compare */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
                <PositionCard
                    side='A'
                    providerName={provA.clinicianName}
                    providerType={PROVIDER_TYPE_LABEL[provA.type]}
                    docTitle={docA?.title}
                    docFile={docA?.fileName}
                    position={conflict.providerAPosition}
                />
                <PositionCard
                    side='B'
                    providerName={provB.clinicianName}
                    providerType={PROVIDER_TYPE_LABEL[provB.type]}
                    docTitle={docB?.title}
                    docFile={docB?.fileName}
                    position={conflict.providerBPosition}
                />
            </div>

            {/* Impacted milestones */}
            {milestones.length > 0 && (
                <div className='bg-white border border-neutral-200 rounded-xl p-4 mb-6'>
                    <h2 className='text-sm font-semibold text-neutral-900 mb-2'>
                        Roadmap milestones on hold
                    </h2>
                    <ul className='space-y-1.5'>
                        {milestones.map((m) => (
                            <li key={m.id} className='flex items-start gap-2 text-sm text-neutral-700'>
                                <span className='text-neutral-400'>·</span>
                                <span>
                                    <span className='font-medium text-neutral-900'>{m.title}</span>
                                    <span className='text-xs text-neutral-500 ml-2'>{m.sequence}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Clarifying thread */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                <div className='flex items-center gap-2 mb-4'>
                    <SparklesIcon className='w-5 h-5 text-brand-600' />
                    <h2 className='text-lg font-semibold text-neutral-900'>Clarifying conversation</h2>
                </div>

                <ul className='space-y-3 mb-4'>
                    {conflict.thread.map((msg, i) => (
                        <MessageRow key={i} msg={msg} />
                    ))}
                </ul>

                {!isResolved && (
                    <div className='border-t border-neutral-100 pt-4'>
                        <label className='block text-xs font-medium text-neutral-700 mb-1.5'>
                            Reply to BrightPath
                        </label>
                        <div className='flex items-start gap-2'>
                            <textarea
                                rows={2}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder='Answer the questions above, or describe what you want to do.'
                                className='flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm'
                            />
                            <button
                                className={
                                    'inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 ' +
                                    'hover:bg-brand-700 text-white text-sm font-medium rounded-lg whitespace-nowrap'
                                }
                            >
                                <PaperAirplaneIcon className='w-4 h-4' />
                                Send
                            </button>
                        </div>
                        <div className='mt-3 flex flex-wrap gap-2'>
                            <SuggestedReply
                                onClick={() => setDraft('Run the speech intensive in June; hold ABA at 20.')}
                                text='Run speech intensive, hold ABA at 20'
                            />
                            <SuggestedReply
                                onClick={() =>
                                    setDraft('Ask both providers to coordinate and recommend a single plan.')
                                }
                                text='Ask both providers to coordinate'
                            />
                            <SuggestedReply
                                onClick={() => setDraft('Step ABA to 25, postpone the speech intensive.')}
                                text='Step ABA to 25, postpone speech'
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function PositionCard({
    side,
    providerName,
    providerType,
    docTitle,
    docFile,
    position,
}: {
    side: 'A' | 'B'
    providerName: string
    providerType: string
    docTitle?: string
    docFile?: string
    position: string
}) {
    return (
        <div className='bg-white border border-neutral-200 rounded-2xl p-5'>
            <div className='flex items-center justify-between mb-2'>
                <span className='text-[10px] uppercase tracking-wider text-neutral-500'>
                    Provider {side}
                </span>
                <span className='text-[10px] uppercase tracking-wider text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded'>
                    {providerType}
                </span>
            </div>
            <h3 className='text-sm font-semibold text-neutral-900'>{providerName}</h3>
            {docTitle && (
                <div className='mt-1 flex items-center gap-1 text-xs text-neutral-500'>
                    <DocumentTextIcon className='w-3 h-3' />
                    <span>
                        {docTitle} ({docFile})
                    </span>
                </div>
            )}
            <p className='mt-3 text-sm text-neutral-800'>{position}</p>
        </div>
    )
}

function MessageRow({ msg }: { msg: ConflictMessage }) {
    const isAi = msg.role === 'ai'
    return (
        <li className={cn('flex gap-3', isAi ? '' : 'flex-row-reverse')}>
            <div
                className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                    isAi ? 'bg-brand-100' : 'bg-neutral-200'
                )}
            >
                {isAi ? (
                    <SparklesIcon className='w-4 h-4 text-brand-700' />
                ) : (
                    <UserCircleIcon className='w-5 h-5 text-neutral-600' />
                )}
            </div>
            <div className={cn('flex-1 min-w-0', isAi ? '' : 'flex flex-col items-end')}>
                <div
                    className={cn(
                        'inline-block px-3 py-2 rounded-2xl text-sm max-w-xl',
                        isAi
                            ? 'bg-brand-50 text-neutral-800 rounded-tl-sm'
                            : 'bg-neutral-100 text-neutral-800 rounded-tr-sm'
                    )}
                >
                    {msg.text}
                </div>
                <div className='text-[10px] text-neutral-400 mt-1'>
                    {isAi ? 'BrightPath AI' : 'You'} · {msg.at}
                </div>
            </div>
        </li>
    )
}

function SuggestedReply({ text, onClick }: { text: string; onClick: () => void }) {
    return (
        <button
            type='button'
            onClick={onClick}
            className='px-2.5 py-1 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-full'
        >
            {text}
        </button>
    )
}
